import compact from 'lodash/compact';
import find from 'lodash/find';
import includes from 'lodash/includes';

import { IAutoQuote } from '../../../interfaces';
import updateValueAtPath from '../../../services/updateValueAtPath';
import getLatestDataOrders from '../getLatestDataOrders';

import getQuoteHealthStatus from './getQuoteHealthStatus';
import { TDataType } from './quoteHealthCheck';

function isOrderReady(
  quote: IAutoQuote,
  dataType: TDataType,
  ineligibleDriverIds: (string | number)[],
  ineligibleVehicleIds: string[]
) {
  const {
    isMissingVins,
    isMissingLienholder,
    isMissingLicenseInfo,
    hasIneligibleDriver,
    hasIneligibleVehicle,
  } = getQuoteHealthStatus(quote, ineligibleDriverIds, ineligibleVehicleIds);
  switch (dataType) {
    case 'vin_services':
      return !isMissingVins && !hasIneligibleVehicle;
    case 'clue':
      return (
        !isMissingLienholder && !isMissingLicenseInfo && !hasIneligibleDriver
      );
    case 'mvr':
      return (
        !isMissingLienholder &&
        !isMissingLicenseInfo &&
        !hasIneligibleDriver &&
        !hasIneligibleVehicle
      );
    default:
      return false;
  }
}

function stageReadyDataOrders(quote: IAutoQuote, dataTypes?: TDataType[]) {
  if (!dataTypes) {
    return quote;
  }

  const newOrders = dataTypes.map((data_type) => ({
    data_type,
    status: 'ready',
  }));
  const orderList = [
    ...(quote?.application?.metadata?.third_party_data_orders || []),
    ...newOrders,
  ];

  const quoteWithNewOrders = updateValueAtPath({
    base: quote,
    path: 'application.metadata.third_party_data_orders',
    updatedValues: orderList,
  });

  return quoteWithNewOrders;
}

export default function handleDataOrders(
  quote: IAutoQuote,
  orderChecklist: TDataType[],
  ineligibleDriverIds: (string | number)[],
  ineligibleVehicleIds: string[]
) {
  const latestOrdersFromThisHealthCheckRun = getLatestDataOrders(
    quote?.application?.metadata?.third_party_data_orders || []
  );

  function hasLatestOrderWithNominalStatus(dataType: TDataType) {
    return find(
      latestOrdersFromThisHealthCheckRun,
      (order) =>
        order?.data_type === dataType &&
        !includes(['error', 'failure'], order.status)
    );
  }

  const isVinOrderReady =
    includes(orderChecklist, 'vin_services') &&
    !hasLatestOrderWithNominalStatus('vin_services') &&
    isOrderReady(
      quote,
      'vin_services',
      ineligibleDriverIds,
      ineligibleVehicleIds
    );
  const isClueOrderReady =
    includes(orderChecklist, 'clue') &&
    !hasLatestOrderWithNominalStatus('clue') &&
    isOrderReady(quote, 'clue', ineligibleDriverIds, ineligibleVehicleIds);
  const isMvrOrderReady =
    includes(orderChecklist, 'mvr') &&
    (includes(orderChecklist, 'clue')
      ? !!find(latestOrdersFromThisHealthCheckRun, {
          data_type: 'clue',
          status: 'success',
        })
      : true) &&
    (includes(orderChecklist, 'vin_services')
      ? !!find(latestOrdersFromThisHealthCheckRun, {
          data_type: 'vin_services',
          status: 'success',
        })
      : true) &&
    !hasLatestOrderWithNominalStatus('mvr') &&
    isOrderReady(quote, 'mvr', ineligibleDriverIds, ineligibleVehicleIds);

  const dataTypes = compact([
    isVinOrderReady && 'vin_services',
    isClueOrderReady && 'clue',
    isMvrOrderReady && 'mvr',
  ]);

  return stageReadyDataOrders(quote, dataTypes);
}
